/**
 * Cache utilities for model management
 * V1.3C: Cache-aware routing
 *
 * Provides utilities to check which models are cached locally
 * and prefer cached models for faster inference.
 */
// === WebLLM Cache Detection ===
/**
 * Check if WebLLM cache utilities are available.
 */
async function getWebLLMCacheUtils() {
    try {
        const webllm = await import('@mlc-ai/web-llm');
        if ('hasModelInCache' in webllm) {
            return {
                hasModelInCache: webllm.hasModelInCache,
            };
        }
        return null;
    }
    catch {
        return null;
    }
}
/**
 * Check if a specific model is in WebLLM's cache.
 */
export async function isModelCached(modelId) {
    const cacheUtils = await getWebLLMCacheUtils();
    if (!cacheUtils) {
        // Can't check WebLLM cache, return false
        return false;
    }
    try {
        return await cacheUtils.hasModelInCache(modelId);
    }
    catch {
        return false;
    }
}
// === Model Store Integration ===
/**
 * List all cached models from SDK store and optionally verify with WebLLM.
 *
 * @param modelStore - SDK model store instance
 * @param manifest - Available models from manifest (for size info)
 * @param verifyWebLLMCache - Whether to verify models are actually in WebLLM cache (slower)
 */
export async function listCachedModels(modelStore, manifest, verifyWebLLMCache = false) {
    // Get records from SDK store
    const records = modelStore.list ? await modelStore.list() : [];
    // Build manifest lookup
    const manifestLookup = new Map();
    if (manifest) {
        for (const model of manifest) {
            manifestLookup.set(model.id, model);
        }
    }
    // Build cache info
    const result = [];
    for (const record of records) {
        const modelInfo = manifestLookup.get(record.modelId);
        let inWebLLMCache = false;
        if (verifyWebLLMCache) {
            inWebLLMCache = await isModelCached(record.modelId);
        }
        else {
            // Assume it's in WebLLM cache if SDK says it's ready
            inWebLLMCache = record.status === 'ready';
        }
        result.push({
            modelId: record.modelId,
            inSDKStore: true,
            inWebLLMCache,
            sdkStatus: record.status,
            sdkRecord: record,
            modelInfo,
            lastUsed: record.updatedAt,
        });
    }
    // Sort by last used (most recent first)
    result.sort((a, b) => (b.lastUsed ?? 0) - (a.lastUsed ?? 0));
    return result;
}
/**
 * Get IDs of models that are ready to use (cached and loaded).
 */
export async function getReadyModelIds(modelStore) {
    const records = modelStore.list ? await modelStore.list() : [];
    return records.filter((r) => r.status === 'ready').map((r) => r.modelId);
}
/**
 * Get cache statistics.
 */
export async function getCacheStats(modelStore, manifest) {
    const cached = await listCachedModels(modelStore, manifest, false);
    let totalSizeMB = 0;
    for (const item of cached) {
        if (item.sdkStatus === 'ready' && item.modelInfo?.sizeMB) {
            totalSizeMB += item.modelInfo.sizeMB;
        }
    }
    // Try to get storage estimate
    let availableStorageMB;
    if (typeof navigator !== 'undefined' && 'storage' in navigator && 'estimate' in navigator.storage) {
        try {
            const estimate = await navigator.storage.estimate();
            if (estimate.quota && estimate.usage) {
                availableStorageMB = Math.round((estimate.quota - estimate.usage) / (1024 * 1024));
            }
        }
        catch {
            // Storage API not available or failed
        }
    }
    return {
        modelsInStore: cached.length,
        modelsReady: cached.filter((c) => c.sdkStatus === 'ready').length,
        modelsInWebLLM: cached.filter((c) => c.inWebLLMCache).length,
        totalSizeMB,
        availableStorageMB,
    };
}
// === Cache-Aware Model Selection ===
/**
 * Filter and prioritize models based on cache status.
 *
 * @param models - All available models
 * @param cachedModelIds - IDs of models that are cached
 * @param preferCached - Whether to strongly prefer cached models
 * @returns Models sorted with cached models prioritized
 */
export function prioritizeCachedModels(models, cachedModelIds, preferCached) {
    const cachedSet = new Set(cachedModelIds);
    // Separate cached and uncached
    const cached = models.filter((m) => cachedSet.has(m.id));
    const uncached = models.filter((m) => !cachedSet.has(m.id));
    if (preferCached) {
        // Strong preference: cached first, then uncached
        return [...cached, ...uncached];
    }
    else {
        // Mild preference: interleave but with cached slightly ahead
        // For now, just put cached first
        return [...cached, ...uncached];
    }
}
/**
 * Check if any cached model can handle the task adequately.
 * Used to decide whether to download a new model or use what's available.
 *
 * @param cachedModels - Cached model info
 * @param taskComplexity - Task complexity level
 * @param preferredTier - Preferred model tier
 */
export function hasAdequateCachedModel(cachedModels, taskComplexity, preferredTier) {
    const readyModels = cachedModels.filter((m) => m.sdkStatus === 'ready' && m.modelInfo);
    if (readyModels.length === 0) {
        return { hasAdequate: false, bestCached: null, reason: 'No cached models available' };
    }
    // Map tier to minimum acceptable tiers based on complexity
    const tierRanking = { fast: 1, balanced: 2, quality: 3 };
    const complexityMinTier = {
        low: 1, // Any tier works for simple tasks
        medium: 2, // Balanced or quality
        high: 3, // Quality preferred
    };
    const minTierRank = complexityMinTier[taskComplexity];
    // Find best cached model that meets requirements
    for (const cached of readyModels) {
        const modelTier = cached.modelInfo.tier ?? 'balanced';
        const modelTierRank = tierRanking[modelTier];
        if (modelTierRank >= minTierRank) {
            return {
                hasAdequate: true,
                bestCached: cached,
                reason: `Cached model ${cached.modelId} (${modelTier}) is adequate for ${taskComplexity} complexity`,
            };
        }
    }
    // No adequate model found, but we have cached models
    const bestAvailable = readyModels[0];
    return {
        hasAdequate: false,
        bestCached: bestAvailable,
        reason: `Best cached model ${bestAvailable.modelId} may not be optimal for ${taskComplexity} complexity`,
    };
}
//# sourceMappingURL=cacheUtils.js.map